import React from 'react';

/* ── Shimmer Bone ──────────────────────────────── */
const B = ({ className = '', style, delay = 0 }) => (
  <div
    className={`skel-bone skel-pill ${className}`}
    style={{ ...style, animationDelay: `${delay}ms` }}
  />
);

const Block = ({ className = '', style, delay = 0 }) => (
  <div
    className={`skel-bone ${className}`}
    style={{ ...style, borderRadius: 12, animationDelay: `${delay}ms` }}
  />
);

const Circle = ({ size = 40, delay = 0 }) => (
  <div
    className="skel-bone skel-round shrink-0"
    style={{ width: size, height: size, animationDelay: `${delay}ms` }}
  />
);

/* ── Skeleton mirroring ExportIntelligenceReact ── */
export const DashboardSkeleton = () => (
  <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="dashboard-skeleton">
    <div className="max-w-[1320px] mx-auto">

      {/* ─── Header: breadcrumb + title ─── */}
      <B className="h-2 w-48 mb-2" delay={0} />
      <B className="h-5 w-[340px] mb-1" delay={40} />

      {/* ─── Metric Toggle: 2 buttons ─── */}
      <div className="flex items-center gap-2 mb-3 mt-2">
        <B className="h-7 w-24 !rounded-md" delay={80} />
        <B className="h-7 w-28 !rounded-md" delay={110} />
      </div>

      {/* ─── Selection bar ─── */}
      <div className="flex items-center gap-3 mb-2">
        <Circle size={8} delay={130} />
        <B className="h-3 w-52" delay={150} />
        <B className="h-2.5 w-40 ml-auto" delay={170} />
      </div>

      {/* ─── Period Filter ─── */}
      <div className="flex items-center gap-2.5 mb-4">
        <B className="h-2.5 w-12" delay={180} />
        <B className="h-8 w-28 !rounded-md" delay={200} />
        <B className="h-2.5 w-4" delay={210} />
        <B className="h-8 w-28 !rounded-md" delay={220} />
        <B className="h-8 w-16 !rounded-md" delay={240} />
        <B className="h-2.5 w-52 ml-auto" delay={260} />
      </div>

      {/* ─── KPI Cards: 3 columns ─── */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="rounded-xl p-4"
            style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}
          >
            <B className="h-2 w-20 mb-3" delay={280 + i * 40} />
            <B className="h-6 w-16 mb-2" delay={300 + i * 40} />
            <B className="h-2 w-24" delay={320 + i * 40} />
          </div>
        ))}
      </div>

      {/* ─── World Map ─── */}
      <div
        className="rounded-xl mt-4 relative overflow-hidden"
        style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)', height: 340 }}
      >
        {/* Zoom controls (top-right) */}
        <div className="absolute top-3 right-3 flex flex-col gap-1 z-10">
          <B className="h-6 w-6 !rounded-md" delay={380} />
          <B className="h-6 w-6 !rounded-md" delay={400} />
          <B className="h-6 w-6 !rounded-md" delay={420} />
        </div>
        {/* Map bubbles placeholder */}
        <div className="absolute inset-0">
          {[
            { x: '22%', y: '30%', s: 52 },
            { x: '48%', y: '24%', s: 30 },
            { x: '73%', y: '28%', s: 40 },
            { x: '56%', y: '42%', s: 22 },
            { x: '32%', y: '52%', s: 18 },
            { x: '82%', y: '50%', s: 28 },
            { x: '64%', y: '22%', s: 16 },
            { x: '18%', y: '58%', s: 14 },
            { x: '42%', y: '38%', s: 24 },
            { x: '76%', y: '42%', s: 12 },
          ].map((b, i) => (
            <div
              key={i}
              className="skel-bone skel-round absolute"
              style={{
                left: b.x, top: b.y,
                width: b.s, height: b.s,
                animationDelay: `${440 + i * 60}ms`,
              }}
            />
          ))}
        </div>
      </div>

      {/* ─── Country Search ─── */}
      <div className="mt-4">
        <B className="h-9 w-full max-w-xs !rounded-lg" delay={500} />
      </div>

      {/* ─── Country Ranking Table ─── */}
      <div
        className="mt-3 rounded-xl overflow-hidden"
        style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}
      >
        {/* Table header */}
        <div className="px-3 py-2.5 flex gap-4 items-center" style={{ background: 'var(--skel-border)' }}>
          <B className="h-2.5 w-5" delay={520} />
          <B className="h-2.5 w-20" delay={530} />
          <B className="h-2.5 w-14 ml-auto" delay={540} />
          <B className="h-2.5 w-14" delay={550} />
          <B className="h-2.5 w-16" delay={560} />
          <B className="h-2.5 w-10" delay={570} />
          <B className="h-2.5 w-10" delay={580} />
        </div>
        {/* Table rows */}
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
          <div
            key={i}
            className="px-3 py-2 flex gap-4 items-center"
            style={{ borderTop: '1px solid var(--skel-border)' }}
          >
            <B className="h-2.5 w-4" delay={600 + i * 30} />
            <B className="h-2.5 w-28" delay={610 + i * 30} />
            <B className="h-2.5 w-14 ml-auto" delay={620 + i * 30} />
            <B className="h-2.5 w-12" delay={630 + i * 30} />
            <B className="h-2.5 w-12" delay={640 + i * 30} />
            <B className="h-2.5 w-10" delay={650 + i * 30} />
            <B className="h-2.5 w-10" delay={660 + i * 30} />
          </div>
        ))}
      </div>

      {/* ─── Customer & Product Donuts, each with a legend list ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {[0, 1].map(i => (
          <div
            key={'donut' + i}
            className="rounded-xl p-4"
            style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}
          >
            <div className="flex items-center justify-between mb-2">
              <B className="h-3.5 w-24" delay={800 + i * 60} />
              <B className="h-4 w-14 !rounded" delay={820 + i * 60} />
            </div>
            {/* Donut ring placeholder */}
            <div className="h-52 flex items-center justify-center">
              <div className="relative">
                <div
                  className="skel-bone skel-round"
                  style={{ width: 152, height: 152, animationDelay: `${850 + i * 60}ms` }}
                />
                {/* Inner hole */}
                <div
                  className="absolute rounded-full"
                  style={{
                    top: 26, left: 26, width: 100, height: 100,
                    background: 'var(--skel-card)',
                  }}
                />
              </div>
            </div>
            {/* Legend list placeholder */}
            <div className="space-y-1.5 mt-1.5">
              {[0, 1, 2, 3, 4].map(j => (
                <div key={j} className="flex items-center gap-2">
                  <div className="skel-bone shrink-0" style={{ width: 9, height: 9, borderRadius: 2, animationDelay: `${900 + i * 60 + j * 20}ms` }} />
                  <B className="h-2.5 flex-1" style={{ maxWidth: `${70 - j * 8}%` }} delay={910 + i * 60 + j * 20} />
                  <B className="h-2.5 w-8" delay={920 + i * 60 + j * 20} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ─── Origin Ports: India map + coast cards + port list ─── */}
      <div className="rounded-xl p-4 mt-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
        <B className="h-3.5 w-64 mb-1.5" delay={1000} />
        <B className="h-2.5 w-full max-w-lg mb-3" delay={1020} />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          {/* India map placeholder */}
          <div className="relative rounded-xl overflow-hidden" style={{ height: 360, background: 'var(--dash-bg)' }}>
            {[
              { x: '48%', y: '20%', s: 30 }, { x: '55%', y: '35%', s: 20 },
              { x: '40%', y: '45%', s: 26 }, { x: '50%', y: '55%', s: 16 },
              { x: '45%', y: '68%', s: 22 }, { x: '58%', y: '30%', s: 12 },
            ].map((b, i) => (
              <div key={i} className="skel-bone skel-round absolute" style={{ left: b.x, top: b.y, width: b.s, height: b.s, animationDelay: `${1040 + i * 50}ms` }} />
            ))}
          </div>
          <div>
            {/* Coast cards ×3 */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[0, 1, 2].map(i => (
                <div key={i} className="rounded-lg p-2.5" style={{ background: 'var(--dash-panel2)', border: '1px solid var(--skel-border)' }}>
                  <B className="h-2 w-14 mb-2" delay={1100 + i * 30} />
                  <B className="h-3.5 w-16 mb-1.5" delay={1110 + i * 30} />
                  <B className="h-2 w-12" delay={1120 + i * 30} />
                </div>
              ))}
            </div>
            {/* Port list rows */}
            <div className="space-y-1.5">
              {[0, 1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="flex items-center gap-2">
                  <div className="skel-bone shrink-0" style={{ width: 8, height: 8, borderRadius: '50%', animationDelay: `${1200 + i * 25}ms` }} />
                  <B className="h-2.5 w-20" delay={1210 + i * 25} />
                  <div className="flex-1 h-2.5 rounded overflow-hidden" style={{ background: 'var(--skel-border)' }}>
                    <B className="h-full !rounded-none" style={{ width: `${90 - i * 10}%` }} delay={1220 + i * 25} />
                  </div>
                  <B className="h-2.5 w-10" delay={1230 + i * 25} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Trend — price & volume ─── */}
      <div className="rounded-xl p-4 mt-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
        <B className="h-3.5 w-44 mb-1.5" delay={1300} />
        <B className="h-2.5 w-72 mb-3" delay={1320} />
        <div className="h-72 relative overflow-hidden rounded-lg" style={{ background: 'var(--dash-bg)' }}>
          <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="none" viewBox="0 0 400 200">
            <polyline points="0,140 40,110 80,120 120,80 160,95 200,55 240,75 280,45 320,65 360,35 400,50" fill="none" stroke="var(--skel-border)" strokeWidth="3" />
            <polyline points="0,170 40,160 80,150 120,155 160,130 200,140 240,120 280,125 320,100 360,110 400,90" fill="none" stroke="var(--skel-border)" strokeWidth="2" strokeDasharray="6 4" />
          </svg>
        </div>
      </div>

      {/* ─── Exporters & Sizes Bar Ranks ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {[0, 1].map(i => (
          <div
            key={'bar' + i}
            className="rounded-xl p-4"
            style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}
          >
            <B className="h-3.5 w-32 mb-4" delay={1400 + i * 60} />
            <div className="space-y-2">
              {[92, 78, 64, 50, 38, 26, 18, 10].map((w, j) => (
                <div key={j} className="flex items-center gap-2">
                  <B className="h-3 w-[140px]" delay={1420 + i * 60 + j * 25} />
                  <div className="flex-1 h-4 rounded overflow-hidden" style={{ background: 'var(--skel-border)' }}>
                    <B
                      className="h-full !rounded-none"
                      style={{ width: `${w}%`, borderRadius: '4px 0 0 4px' }}
                      delay={1430 + i * 60 + j * 25}
                    />
                  </div>
                  <B className="h-3 w-[72px]" delay={1440 + i * 60 + j * 25} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
